import React, { useCallback, useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { getRecoil, resetRecoil, setRecoil } from "recoil-nexus";
import styled from "styled-components";
import ApiController from "../../controller/ApiController";
import LogController from "../../controller/LogController";
import { authState } from "../../recoil/auth";
import { eventState, EventStateType } from "../../recoil/event";
import { langState, langText } from "../../recoil/lang";
import { FindItemAudio } from "../../utils/Audio";

const findingItem = [
  {
    type: "image",
    item: `${process.env.PUBLIC_URL}/knockknock/img/game/find_item01.png`,
    correct: `${process.env.PUBLIC_URL}/knockknock/img/game/find_correct01.png`,
    level: 0,
  },
  {
    type: "image",
    item: `${process.env.PUBLIC_URL}/knockknock/img/game/find_item02.png`,
    correct: `${process.env.PUBLIC_URL}/knockknock/img/game/find_correct02.png`,
    level: 1,
  },
  {
    type: "image",
    item: `${process.env.PUBLIC_URL}/knockknock/img/game/find_item03.png`,
    correct: `${process.env.PUBLIC_URL}/knockknock/img/game/find_correct03.png`,
    level: 2,
  },
  {
    type: "image",
    item: `${process.env.PUBLIC_URL}/knockknock/img/game/find_item04.png`,
    correct: `${process.env.PUBLIC_URL}/knockknock/img/game/find_correct04.png`,
    level: 3,
  },
];

// 오디오
const FindItem = () => {
  let couponId = 90;
  const [event, setEvent] = useRecoilState<EventStateType>(eventState);
  const [lang, setLang] = useRecoilState<number>(langState);
  const [isStart, setIsStart] = useState<boolean>(false);
  const [isEnd, setIsEnd] = useState<boolean>(false);
  const [randomItem, setRandomItem] = useState<any[]>([]);
  const [startTimer, setStartTimer] = useState<number>(0);
  const [couponPrice, setCouponPrice] = useState<number>(0);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [isCoupon, setIsCoupon] = useState<number>(-1);
  const [currentGameIndex, setCurrentGameIndex] = useState<number>(0);

  // 게임 정답, 오답, 최종 쿠폰 발급 화면
  const resultData = {
    1: {
      imgSrc: `${process.env.PUBLIC_URL}/knockknock/svg/ic_correct.svg`,
      alt: "정답",
      title: `${langText.game_correct[lang]}`,
      description:
        currentGameIndex !== 3 ? `${langText.next_game[lang]}` : null,
    },
    0: {
      imgSrc: `${process.env.PUBLIC_URL}/knockknock/svg/ic_wrong.svg`,
      alt: "틀림",
      title: `${langText.wrong_game[lang]}`,
      description: `${langText.try_again_game[lang]}`,
    },

    end: (couponPrice: number) => {
      return {
        imgSrc: `${process.env.PUBLIC_URL}/knockknock/svg/ic_get_coupon.svg`,
        alt: "쿠폰 발급",
        title: `${langText.total[lang]} ${couponPrice.toLocaleString()}원`,
        description: `${langText.get_findItem_coupon[lang]}`,
      };
    },
  };
  const renderResult = (key: keyof typeof resultData) => {
    const data = key === "end" ? resultData[key](couponPrice) : resultData[key];
    const { imgSrc, alt, title, description } = data;
    return (
      <div className={`resultWrap ${key === "end" ? "end" : ""}`}>
        <img src={imgSrc} alt={alt} />
        <h2>{title}</h2>
        {description && <p>{description}</p>}
      </div>
    );
  };

  // 그림 랜덤 로직
  const insertRandomly = (arr: any[], item: any) => {
    const randomIndex = Math.floor(Math.random() * (arr.length + 1));
    return [...arr.slice(0, randomIndex), item, ...arr.slice(randomIndex)];
  };
  const settingItem = useCallback((index: number) => {
    const { type, item, correct, level } = findingItem[index];
    const baseItems = Array.from({ length: 8 }, () => ({
      type,
      item,
      value: false,
      level,
    }));
    const correctItem = { type, item: correct, value: true, level };
    setRandomItem(insertRandomly(baseItems, correctItem));
  }, []);

  // 게임 시작버튼
  const startOrReset = () => {
    playAudio("start");
    setIsStart((prev) => {
      const nextStart = !prev;
      if (nextStart) {
        setStartTimer(3);
        settingItem(0);
      } else {
        resetGame(0);
      }
      return nextStart;
    });
  };
  const resetGame = async (level: number) => {
    setTimeLeft(0);
    setCurrentGameIndex(0);
    setTimeout(async () => {
      if (level > 0) {
        await setEventCoupon(level);
      } else {
        resetRecoil(eventState);
      }

      setIsStart(false);
      stopAudio("bgm");
      stopAudio("start");
      stopAudio("correct");
      stopAudio("wrong");
      setStartTimer(0);
      setTimeLeft(0);
    }, 2000);
  };

  // 그림 클릭했을때
  const selectItem = (item: any) => {
    if (timeLeft <= 0) return;
    const correct = item.value;
    setIsCoupon(correct ? (isCoupon >= 0 ? isCoupon + 1 : 1) : 0);
    playAudio(correct ? "correct" : "wrong");

    setTimeout(() => {
      if (correct) {
        handleNextGame();
      }
    }, 2000);
    setTimeLeft(0);
  };

  const playAudio = (type: "bgm" | "start" | "correct" | "wrong") => {
    const audio = FindItemAudio[type];
    audio.currentTime = 0;
    audio.play().catch((err) => {
      console.error("Audio play error:", err);
    });
  };
  const stopAudio = (type: "bgm" | "start" | "correct" | "wrong") => {
    const audio = FindItemAudio[type];
    if (!audio.paused) {
      audio.pause();
      audio.currentTime = 0;
    }
  };

  // 다음 게임 불러오는 로직(이미지 다 불러올때까지 대기)
  const preloadImage = (src: string) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.src = src;
      img.onload = () => resolve(img);
      img.onerror = (error) => reject(error);
    });
  const preloadResources = async (index: number) => {
    const { item, correct } = findingItem[index];
    await Promise.all([preloadImage(item), preloadImage(correct)]);
  };

  const handleNextGame = async () => {
    setIsCoupon(-1);

    if (currentGameIndex < findingItem.length - 1) {
      const nextIndex = currentGameIndex + 1;

      await preloadResources(nextIndex);
      setCurrentGameIndex(nextIndex);
      settingItem(nextIndex);
      setTimeLeft(8 - nextIndex);
    } else {
      // 모두 성공했을 때
      resetGame(4);
    }
  };

  const setEventCoupon = async (level: number) => {
    try {
      if (event.event_id) {
        if (level === 0) {
          couponId = 90;
        } else if (level === 1) {
          couponId = 91;
        } else if (level === 2) {
          couponId = 92;
        } else if (level === 3) {
          couponId = 93;
        } else if (level === 4) {
          couponId = 94;
        }

        const response = await ApiController.store.setEventCoupon(
          event.event_id,
          getRecoil(authState).user_id,
          getRecoil(authState).user_token_id,
          couponId
        );

        if (response && response.data) {
          setCouponPrice(response.data.coupon.price);
          setIsEnd(true);

          LogController.event({
            path: "SurpriseModal",
            params: "event",
            params2:
              "틀린그림찾기 당첨 (금액: " + response.data.coupon.price + ")",
            index: event.event_id,
          });
        }
        setTimeout(() => {
          resetRecoil(eventState);
        }, 4000);
      }
    } catch (err) {
      console.log("getAdverList err-->", err);
    }
  };

  useEffect(() => {
    if (startTimer > 0) {
      const timer = setTimeout(() => setStartTimer((prev) => prev - 1), 1000);
      return () => clearTimeout(timer);
    } else if (startTimer === 0 && isStart) {
      setTimeLeft(8);
    }
  }, [startTimer, isStart]);

  useEffect(() => {
    if (isStart && timeLeft > 0) {
      const interval = setInterval(() => {
        setTimeLeft((prev) => Math.max(prev - 1, 0));
      }, 1000);
      return () => clearInterval(interval);
    } else if (timeLeft === 0 && startTimer === 0 && isStart && isCoupon < 1) {
      // 시간초과
      console.log("시간초과 or 틀렸을때");
      playAudio("wrong");
      setIsCoupon(0);
      resetGame(currentGameIndex);
    }
  }, [isStart, timeLeft]);

  useEffect(() => {
    const handleAudioPlay = () => {
      FindItemAudio.bgm.currentTime = 0;
      FindItemAudio.bgm.play().catch((error) => {
        console.error("Error playing audio", error);
      });
    };

    if (isStart && startTimer === 0 && isCoupon !== 0) {
      handleAudioPlay();
      FindItemAudio.bgm.addEventListener("ended", handleAudioPlay);
    } else {
      FindItemAudio.bgm.pause();
      FindItemAudio.bgm.currentTime = 0;
    }

    return () => {
      FindItemAudio.bgm.removeEventListener("ended", handleAudioPlay);
      FindItemAudio.bgm.pause();
      FindItemAudio.bgm.currentTime = 0;
    };
  }, [isStart, startTimer, isCoupon]);

  return (
    <Style>
      <div className="wrap">
        {isStart ? (
          <div className="gameWrap">
            {startTimer > 0 ? (
              <div className="startNoticeWrap">
                <h1 className="eventText">
                  {langText.game_start[lang]}
                  <br />
                  {startTimer}
                </h1>
              </div>
            ) : (
              <div className="gameContent">
                <div className="timerWrap">
                  <h1 className="eventText">
                    {langText.find_item_title[lang]}
                  </h1>
                  <p> {langText.time_left[lang]}</p>
                  <h4>{timeLeft > 0 ? Math.ceil(timeLeft) : 0}</h4>
                </div>
                <div className="gameListWrap">
                  {randomItem.map((item, index) => (
                    <div
                      className="listItem"
                      key={index}
                      onClick={() => selectItem(item)}
                    >
                      {item.type === "image" && (
                        <img src={item.item} alt={`option-${index}`} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="startWrap">
            {lang === 0 ? (
              <img
                src={`${process.env.PUBLIC_URL}/knockknock/img/game/find_startscreen.png`}
                alt="시작화면"
                width="100%"
              />
            ) : (
              <img
                src={`${process.env.PUBLIC_URL}/knockknock/img/game/find_startscreen_foreign.png`}
                alt="시작화면"
                width="100%"
              />
            )}
            <video className="choiceGif" autoPlay preload="auto" muted loop>
              <source
                src={`${process.env.PUBLIC_URL}/knockknock/mp4/find_choice.mp4`}
                type="video/mp4"
              />
            </video>
            <div className="gameButtonWrap">
              <button className="startButton eventText" onClick={startOrReset}>
                <h2>{langText.game_start[lang]}</h2>
              </button>
              <button
                className="closeButton eventText"
                onClick={() => {
                  resetRecoil(eventState);
                }}
              >
                <h2>{langText.game_end[lang]}</h2>
              </button>
            </div>
          </div>
        )}

        {isCoupon === 0 && renderResult(0)}
        {isCoupon === 1 && renderResult(1)}
        {isEnd && renderResult("end")}
      </div>
    </Style>
  );
};
export default FindItem;

const Style = styled.div`
  @keyframes zoomIn {
    from {
      scale: 0.6;
    }
    to {
      scale: 1;
    }
  }
  h1 {
    font-size: 60px;
    font-weight: 400;
    color: #fff;
    line-height: 84px;
  }
  h2 {
    font-size: 40px;
    font-weight: 400;
    line-height: 44px;
    color: #fff;
  }
  h4 {
    font-size: 88px;
    font-weight: 600;
    color: #fff;
  }
  p {
    font-size: 16px;
    font-weight: 300;
    color: #fff;
  }

  .wrap {
    width: 100vw;
    height: 100vh;
    max-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    padding: 40px 4vw;
    gap: 16px;
  }

  /* startNoticeWrap */
  .startNoticeWrap {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
  }

  /* startWrap */
  .startWrap {
    position: relative;
  }
  .gameButtonWrap {
    position: absolute;
    bottom: 10%;
    right: 50%;
    left: 50%;
    transform: translate(-50%, 10%);
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 20px;
  }
  .startButton,
  .closeButton {
    padding: 25px 40px 20px 40px;
    background: #fff;
    border-radius: 50px;
    width: 60%;

    h2 {
      white-space: nowrap;
      color: #4dd2ae;
    }
  }
  .closeButton {
    background: none;
    border: 2px solid #fff;

    h2 {
      color: #fff;
    }
  }
  .choiceGif {
    width: 80%;
    position: absolute;
    bottom: 35%;
    right: 50%;
    left: 50%;
    transform: translate(-50%, 32%);
  }

  /* gameWrap */
  .gameWrap {
    width: 100%;
    height: 100vh;
    border-radius: 20px;
    background: #2bb590;
  }
  .gameContent {
    height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
  }
  .timerWrap {
    margin: 0 auto;
    padding-top: 17%;
    text-align: center;

    h1 {
      margin-bottom: 50px;
    }
    h4 {
      margin-top: 2px;
    }
  }
  .gameListWrap {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    flex: 1;
    padding: 30px 15% 80px 15%;
    position: relative;

    .listItem {
      width: 26%;
      display: flex;
      align-items: center;
      h2 {
        font-size: 24px;
        line-height: 28px;
        font-weight: 700;
        color: #fff;
      }
      img {
        width: 100%;
      }
    }
  }

  /* resultWrap */
  .resultWrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #0000004d;

    h2 {
      margin-top: 60px;
      margin-bottom: 20px;
      font-size: 80px;
      font-weight: 600;
      color: #fff;
      line-height: 80px;
    }
    p {
      font-size: 32px;
      font-weight: 400;
    }
    .correctImage {
      width: 100%;
      min-width: 300px;
    }
  }
  .resultWrap.end {
    background: #000;
  }

  /* .coupon {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    transform-origin: center;
    width: fit-content;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    background: #4dd2ae;
    padding: 10px 20px;
    scale: 1;
    z-index: 2;
    h2 {
      color: #fff;
      font-size: 32px;
      line-height: 40px;
      font-weight: 600;
      white-space: pre-line;
      text-align: center;
    }
  } */
`;
